
import { computed, defineComponent, inject, ref, watch } from 'vue';
import {
    COUNTRIES,
    END_USER_JOB_FUNCTIONS,
    isFreeEmailDomain,
    ISignupFormState,
    JOB_FUNCTIONS,
    US_STATES
} from '../signupFlow';
import { IEmailCheckResult, ISignupService } from '../signupService';
import {
    PhArrowRight,
    PhCaretDown,
    PhEnvelopeSimple,
    PhPhone,
    PhUser
} from '@phosphor-icons/vue';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default defineComponent({
    name: 'StepAboutYou',
    components: { PhArrowRight, PhCaretDown, PhEnvelopeSimple, PhPhone, PhUser },
    emits: ['next', 'end-user-role'],
    setup(props, { emit }) {
        const form = inject('signupForm') as ISignupFormState;
        const service = inject('signupService') as ISignupService;
        const showErrors = ref(false);
        // Set when the API says the email already has an account; cleared as
        // soon as the email changes.
        const emailInUse = ref(false);
        // Set when the API says the address is on a free or disposable
        // provider. The full list lives on the server; the local check above
        // only catches gmail.com without a round trip.
        const emailBlocked = ref(false);
        const checking = ref(false);
        watch(() => form.email, () => {
            emailInUse.value = false;
            emailBlocked.value = false;
        });

        const isUS = computed(() => form.country === 'United States');

        const emailError = computed(() => {
            if (!form.email) return 'This field is required.';
            if (!EMAIL_PATTERN.test(form.email)) return 'Please enter a valid email address.';
            if (isFreeEmailDomain(form.email)) {
                return 'Please use your work or school email address.';
            }
            return '';
        });

        // FLOCK-6777: check the full free-email list, and existing accounts, as
        // soon as the user leaves the Email field, so a blocked address is
        // flagged right away like gmail.com instead of only on Next. A result is
        // kept for the address it was made for and reused by Next; a failed
        // check ('unchecked') is not kept, so Next tries again.
        let lastCheck: { email: string; result: IEmailCheckResult } | null = null;

        async function checkEmailFor(email: string): Promise<IEmailCheckResult> {
            if (lastCheck && lastCheck.email === email) return lastCheck.result;
            const result = await service.checkEmail(email);
            if (result.reason !== 'unchecked') lastCheck = { email, result };
            return result;
        }

        function showCheckResult(result: IEmailCheckResult): void {
            if (result.available) return;
            if (result.reason === 'free_email') emailBlocked.value = true;
            else emailInUse.value = true;
        }

        async function handleEmailBlur() {
            // Empty, malformed and gmail.com addresses are already handled locally.
            if (emailError.value) return;
            const email = form.email;
            const result = await checkEmailFor(email);
            // The address changed while the check was in flight; its result no
            // longer applies.
            if (form.email !== email) return;
            showCheckResult(result);
        }

        const isValid = computed(
            () =>
                !!form.firstName &&
                !!form.lastName &&
                !emailError.value &&
                !!form.jobFunction &&
                !!form.phone &&
                !!form.country &&
                (!isUS.value || !!form.state)
        );

        async function handleNext() {
            // End-user branch: students and teachers never create accounts.
            // Job function alone decides the reroute (before full validation),
            // matching the prototype behavior.
            if (END_USER_JOB_FUNCTIONS.includes(form.jobFunction)) {
                emit('end-user-role');
                return;
            }
            if (!isValid.value) {
                showErrors.value = true;
                return;
            }
            // Duplicate email is flagged here, where the email is typed, not on
            // the Organization step. An abandoned earlier sign-up is not a
            // duplicate: registration replaces it.
            checking.value = true;
            const check = await checkEmailFor(form.email);
            checking.value = false;
            if (!check.available) {
                showCheckResult(check);
                return;
            }
            service.submitLead(form);
            emit('next');
        }

        return {
            form,
            showErrors,
            emailInUse,
            emailBlocked,
            handleEmailBlur,
            checking,
            isUS,
            emailError,
            handleNext,
            jobFunctions: JOB_FUNCTIONS,
            countries: COUNTRIES,
            usStates: US_STATES
        };
    }
});
